import { sec } from './units.ts';

export type HeroClass = 'warrior' | 'artificer' | 'mage' | 'ranger' | 'cleric' | 'barbarian' | 'paladin' | 'druid';
export const HERO_CLASSES: HeroClass[] = ['warrior', 'artificer', 'mage', 'ranger', 'cleric', 'barbarian', 'paladin', 'druid'];
/** The classes players can pick (and hire) now: the barbarian, paladin and druid join once they are finished. */
export const PLAYABLE_CLASSES: HeroClass[] = HERO_CLASSES;
/** The new classes' own buildings: in the build menu only once their class can be picked. */
export const CLASS_BUILDINGS: Record<string, HeroClass> = { temple: 'paladin', grove: 'druid', hall: 'barbarian' };

export interface HeroDef {
  cls: HeroClass;
  hp: number;
  attack: number;
  /** Basic attack reload in ticks. */
  reload: number;
  /** Basic attack range (fixed). */
  range: number;
  /** Splash of the basic attack (fixed), 0 = single target. */
  splash: number;
  armorBp: number;
  /** Speed in fixed units per second. */
  speed: number;
  /** Basic attack projectile speed (fixed per tick), 0 = melee/instant. */
  projectileSpeed: number;
  /** How many ground nightmares this hero can hold in melee at once. */
  blockCapacity: number;
  skills: [SkillId, SkillId, SkillId];
}

export type SkillId =
  | 'shieldBash'
  | 'challenge'
  | 'oath'
  | 'turret'
  | 'gearGrenade'
  | 'automaton'
  | 'fireball'
  | 'frostWall'
  | 'storm'
  | 'multishot'
  | 'net'
  | 'arrowRain'
  | 'mend'
  | 'sanctuary'
  | 'dawn'
  | 'whirl'
  | 'leap'
  | 'frenzy'
  | 'smite'
  | 'aegis'
  | 'hallow'
  | 'entangle'
  | 'bearForm'
  | 'bloom'
  // Learned in the tree (docs/PODERES.md §7): the second skill of a slot.
  | 'decoy'
  | 'frostOrb'
  | 'hawk'
  | 'guardTotem'
  | 'spinHammer'
  | 'clanRoar'
  | 'repairDrone'
  | 'wasps'
  | 'lowBlow'
  | 'chainLightning'
  | 'poisonArrow'
  | 'faithRay'
  | 'throwingAxe'
  | 'blindingLight'
  | 'spikeMine'
  | 'charge'
  | 'deepRoot'
  // Learned ultimates (level 10).
  | 'guardBanner'
  | 'blizzard'
  | 'titanArrow'
  | 'dawnJudgment'
  | 'holyRampart'
  | 'fieldMortar'
  | 'plague'
  // Learned from a tome (docs/PODERES.md §6): the short slot's, from the crypts.
  | 'grapple'
  | 'tuneUp'
  | 'frostLance'
  | 'piercingShot'
  | 'renew'
  | 'brutalBlow'
  | 'holySeal'
  | 'briarLash'
  // The medium slot's, from the incursions.
  | 'defyShout'
  | 'teslaCoil'
  | 'blink'
  | 'snare'
  | 'sharedPrayer'
  | 'bloodletting'
  | 'intervene'
  | 'thornWall'
  // The ultimate slot's, from Marea bosses and saga chapters.
  | 'lastStand'
  | 'clockHound'
  | 'fireRain'
  | 'eagleEye'
  | 'intercession'
  | 'ancestralFury'
  | 'avatarOfLight'
  | 'forestAwakening'
  // The third of each slot (levels 14, 18 and 22).
  | 'shieldSlam'
  | 'shieldWall'
  | 'shieldEcho'
  | 'flamethrower'
  | 'ironBarricade'
  | 'fortifyAll'
  | 'acidDart'
  | 'shadowChains'
  | 'dreamRift'
  | 'disengage'
  | 'signalFlare'
  | 'hiddenShot'
  | 'piercingLight'
  | 'cleanse'
  | 'choir'
  | 'kick'
  | 'terrify'
  | 'duel'
  | 'atone'
  | 'blessWeapon'
  | 'sacrifice'
  | 'sporeCloud'
  | 'wolfForm'
  | 'rootQuake';

/**
 * 'ally': a hero (the caster too); without one, the most hurt in reach. 'guard': a hero, or the
 * structure (or the keep) under a point; without either, the most hurt hero in reach.
 */
export type SkillTargeting = 'self' | 'enemy' | 'point' | 'structure' | 'ally' | 'guard';

/**
 * How a spectre or a bot decides to use a skill that has no code of its own in their AI (docs/PODERES.md §8):
 * - pack: at the thickest pack of at least `n` nightmares within its reach;
 * - around: when `n` nightmares are within its radius of the hero;
 * - fight: whenever a nightmare is within `reach` of the hero;
 * - allies: when `n` allies (the hero too) stand within its radius with nightmares on them;
 * - repair: when `n` structures within `reach` of the hero are hurt (below half their health);
 * - heal: at the most hurt ally in its reach, when `n` allies are below 70 % of their life.
 */
export interface SkillAi {
  use: 'pack' | 'around' | 'fight' | 'allies' | 'repair' | 'heal';
  n: number;
  reach?: number;
}

export interface SkillDef {
  id: SkillId;
  cls: HeroClass;
  slot: 0 | 1 | 2;
  cooldown: number;
  targeting: SkillTargeting;
  /** Cast range (fixed) for targeted skills. */
  castRange: number;
  damage: number;
  radius: number;
  duration: number;
  /** Stun / root ticks. */
  stun: number;
  slowBp: number;
  /** Generic magnitude (heal amount, fire-rate bonus, projectile count…). */
  amount: number;
  /** Interval between pulses for channelled area skills. */
  every: number;
  /** For a learned skill: how the spectres and bots use it. */
  ai?: SkillAi;
}

const s0 = { castRange: 0, damage: 0, radius: 0, duration: 0, stun: 0, slowBp: 0, amount: 0, every: 0 };

export const SKILLS: Record<SkillId, SkillDef> = {
  shieldBash: { ...s0, id: 'shieldBash', cls: 'warrior', slot: 0, cooldown: sec(8), targeting: 'enemy', castRange: 1300, damage: 40, stun: 12 },
  challenge: { ...s0, id: 'challenge', cls: 'warrior', slot: 1, cooldown: sec(14), targeting: 'self', radius: 4500, duration: sec(4) },
  oath: { ...s0, id: 'oath', cls: 'warrior', slot: 2, cooldown: sec(60), targeting: 'self', radius: 4000, duration: sec(5) },
  turret: { ...s0, id: 'turret', cls: 'artificer', slot: 0, cooldown: sec(6), targeting: 'point', castRange: 2500, damage: 8, every: 12, duration: sec(20), amount: 2, radius: 3500 },
  /** Sticks where it lands, drags the nightmares around it in for `duration`, then bursts. */
  gearGrenade: { ...s0, id: 'gearGrenade', cls: 'artificer', slot: 1, cooldown: sec(12), targeting: 'point', castRange: 5500, damage: 55, radius: 1800, duration: 24 },
  /** A clockwork automaton planted at a point: it draws the nightmares around it, sweeps them and quickens nearby towers. */
  automaton: { ...s0, id: 'automaton', cls: 'artificer', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 3000, duration: sec(20), damage: 24, every: 12, radius: 1300, amount: 600 },
  fireball: { ...s0, id: 'fireball', cls: 'mage', slot: 0, cooldown: sec(7), targeting: 'point', castRange: 6000, damage: 60, radius: 1500 },
  frostWall: { ...s0, id: 'frostWall', cls: 'mage', slot: 1, cooldown: sec(15), targeting: 'point', castRange: 5000, amount: 4, slowBp: 6000, duration: sec(5) },
  storm: { ...s0, id: 'storm', cls: 'mage', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 6000, damage: 120, radius: 3000, duration: sec(4), every: 4 },
  multishot: { ...s0, id: 'multishot', cls: 'ranger', slot: 0, cooldown: sec(6), targeting: 'point', castRange: 6000, damage: 18, amount: 5, radius: 2500 },
  net: { ...s0, id: 'net', cls: 'ranger', slot: 1, cooldown: sec(12), targeting: 'enemy', castRange: 6000, stun: 24 },
  arrowRain: { ...s0, id: 'arrowRain', cls: 'ranger', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 7000, damage: 10, every: 6, duration: sec(4), radius: 2500 },
  // Cleric: mend heals one hero (amount = HP); sanctuary is ground that heals the heroes on it every
  // pulse; dawn raises the fallen around the cleric and heals everyone there (amount = bp of max HP),
  // untouchable for a moment (duration).
  mend: { ...s0, id: 'mend', cls: 'cleric', slot: 0, cooldown: sec(8), targeting: 'ally', castRange: 5500, amount: 75 },
  sanctuary: { ...s0, id: 'sanctuary', cls: 'cleric', slot: 1, cooldown: sec(16), targeting: 'point', castRange: 5000, radius: 2200, duration: sec(6), every: 12, amount: 8 },
  dawn: { ...s0, id: 'dawn', cls: 'cleric', slot: 2, cooldown: sec(60), targeting: 'self', radius: 5000, duration: sec(2), amount: 3000 },
  // Barbarian: the whirl hits all around; the leap lands on a point and stuns there; the frenzy
  // (duration) is unstoppable, quicker and drinks life from its blows (numbers in BARBARIAN).
  whirl: { ...s0, id: 'whirl', cls: 'barbarian', slot: 0, cooldown: sec(6), targeting: 'self', radius: 1400, damage: 34 },
  leap: { ...s0, id: 'leap', cls: 'barbarian', slot: 1, cooldown: sec(12), targeting: 'point', castRange: 5000, radius: 1300, damage: 40, stun: 12 },
  frenzy: { ...s0, id: 'frenzy', cls: 'barbarian', slot: 2, cooldown: sec(60), targeting: 'self', duration: sec(8) },
  // Paladin: the smite hits one nightmare (twice as hard on the corrupt); the aegis shields a hero or a
  // structure (amount = HP it soaks, for duration); hallowed ground weakens and burns (numbers in PALADIN) and
  // heals the heroes on it (amount = bp of their life every pulse). The smite falls from the sky: it reaches far.
  smite: { ...s0, id: 'smite', cls: 'paladin', slot: 0, cooldown: sec(8), targeting: 'enemy', castRange: 5000, damage: 70 },
  aegis: { ...s0, id: 'aegis', cls: 'paladin', slot: 1, cooldown: sec(14), targeting: 'guard', castRange: 5000, amount: 150, duration: sec(6) },
  hallow: { ...s0, id: 'hallow', cls: 'paladin', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 4000, radius: 2500, duration: sec(6), every: 12, damage: 15, amount: 150 },
  // Druid: roots hold an area (stun = ticks held, flyers brought down); the bear lasts duration (numbers
  // in DRUID); the bloom is a grove around the druid that heals the heroes in it every pulse (amount =
  // bp of their max HP), never raising the fallen.
  entangle: { ...s0, id: 'entangle', cls: 'druid', slot: 0, cooldown: sec(7), targeting: 'point', castRange: 5500, radius: 1500, damage: 35, stun: sec(2) },
  bearForm: { ...s0, id: 'bearForm', cls: 'druid', slot: 1, cooldown: sec(18), targeting: 'self', duration: sec(5), damage: 45, radius: 900 },
  bloom: { ...s0, id: 'bloom', cls: 'druid', slot: 2, cooldown: sec(60), targeting: 'self', radius: 4000, duration: sec(8), every: 12, amount: 300 },
  // Learned skills (docs/PODERES.md §7; their own numbers in SKILL_FX).
  /** A dummy with a banner planted at a point: it draws the nightmares around (radius) and holds (amount = HP) for duration. */
  decoy: { ...s0, id: 'decoy', cls: 'warrior', slot: 1, cooldown: sec(14), targeting: 'point', castRange: 4000, radius: 3000, duration: sec(10), amount: 450, ai: { use: 'pack', n: 3 } },
  /** A slow orb of frost rolls to a point and lingers: it slows what it touches and bites every pulse. */
  frostOrb: { ...s0, id: 'frostOrb', cls: 'mage', slot: 1, cooldown: sec(12), targeting: 'point', castRange: 6000, radius: 1000, damage: 12, every: 6, slowBp: 5000, duration: sec(1), ai: { use: 'pack', n: 3 } },
  /** A hawk flies out and hunts for duration: it strikes the nearest nightmare every pulse and marks it (it takes more from everyone). */
  hawk: { ...s0, id: 'hawk', cls: 'ranger', slot: 1, cooldown: sec(15), targeting: 'self', radius: 6000, damage: 14, every: 12, duration: sec(15), ai: { use: 'fight', n: 1, reach: 6000 } },
  /** A totem planted at a point: allies around it take less while it stands (duration). */
  guardTotem: { ...s0, id: 'guardTotem', cls: 'cleric', slot: 1, cooldown: sec(16), targeting: 'point', castRange: 5000, radius: 2500, duration: sec(15), ai: { use: 'allies', n: 2 } },
  /** A holy hammer whirls around the paladin for duration, striking everything in reach every pulse. */
  spinHammer: { ...s0, id: 'spinHammer', cls: 'paladin', slot: 1, cooldown: sec(14), targeting: 'self', radius: 1500, damage: 20, every: 6, duration: sec(6), ai: { use: 'around', n: 3 } },
  /** The clan's roar: every ally around takes less and hits harder for duration (numbers in SKILL_FX). */
  /** A clockwork drone flies to the most hurt structures within its radius of the artificer and mends them (amount each pulse) for duration. */
  repairDrone: { ...s0, id: 'repairDrone', cls: 'artificer', slot: 1, cooldown: sec(14), targeting: 'self', radius: 6000, duration: sec(12), every: 12, amount: 25, ai: { use: 'repair', n: 1, reach: 6000 } },
  /** A cloud of wasps on one nightmare: it poisons it (damage each pulse) for duration. */
  wasps: { ...s0, id: 'wasps', cls: 'druid', slot: 0, cooldown: sec(7), targeting: 'enemy', castRange: 6000, damage: 6, every: 6, duration: sec(6), ai: { use: 'fight', n: 1, reach: 6000 } },
  /** A low blow at one nightmare: damage, and it takes more from everyone for duration (SKILL_FX.lowBlow). */
  lowBlow: { ...s0, id: 'lowBlow', cls: 'warrior', slot: 0, cooldown: sec(7), targeting: 'enemy', castRange: 1300, damage: 25, duration: sec(4), ai: { use: 'fight', n: 1, reach: 1300 } },
  /** Lightning that jumps from one nightmare to the next (amount jumps within radius), a little weaker each time. */
  chainLightning: { ...s0, id: 'chainLightning', cls: 'mage', slot: 0, cooldown: sec(7), targeting: 'enemy', castRange: 6000, damage: 40, amount: 4, radius: 2500, ai: { use: 'pack', n: 2 } },
  /** A poisoned arrow: the poison runs on its nightmare (damage each pulse) for duration. */
  poisonArrow: { ...s0, id: 'poisonArrow', cls: 'ranger', slot: 0, cooldown: sec(6), targeting: 'enemy', castRange: 6000, damage: 8, every: 6, duration: sec(6), ai: { use: 'fight', n: 1, reach: 6000 } },
  /** A ray of light to a point: it hurts the nightmares in its way and heals the allies in it (amount). */
  faithRay: { ...s0, id: 'faithRay', cls: 'cleric', slot: 0, cooldown: sec(6), targeting: 'point', castRange: 5000, radius: 600, damage: 30, amount: 120, ai: { use: 'heal', n: 1 } },
  /** An axe thrown at a nightmare far off: it strikes going and coming back. */
  throwingAxe: { ...s0, id: 'throwingAxe', cls: 'barbarian', slot: 0, cooldown: sec(6), targeting: 'enemy', castRange: 5000, damage: 36, ai: { use: 'fight', n: 1, reach: 5000 } },
  /** A blinding light ahead (a cone of radius): it burns the nightmares in it (holy) and they hit half as hard for duration. */
  blindingLight: { ...s0, id: 'blindingLight', cls: 'paladin', slot: 0, cooldown: sec(8), targeting: 'point', castRange: 2500, radius: 2500, damage: 30, duration: sec(3), ai: { use: 'fight', n: 1, reach: 2200 } },
  /** A mine of nails at a point: the first nightmare to step on it sets it off (damage around, a slow). */
  spikeMine: { ...s0, id: 'spikeMine', cls: 'artificer', slot: 0, cooldown: sec(6), targeting: 'point', castRange: 4000, damage: 80, radius: 1400, slowBp: 5000, duration: sec(30), amount: 3, ai: { use: 'pack', n: 2 } },
  /** A charge to a point: the barbarian runs through, striking and stunning everything in its way. */
  charge: { ...s0, id: 'charge', cls: 'barbarian', slot: 1, cooldown: sec(12), targeting: 'point', castRange: 5000, radius: 900, damage: 40, stun: 12, ai: { use: 'pack', n: 2 } },
  /** Deep roots on an ally: it can't be held or thrown, and heals (amount = bp of its life each second) for duration. */
  deepRoot: { ...s0, id: 'deepRoot', cls: 'druid', slot: 1, cooldown: sec(16), targeting: 'ally', castRange: 5000, duration: sec(6), every: 12, amount: 300, ai: { use: 'allies', n: 1 } },
  // From the tomes (short slot).
  /** A chain thrown at a nightmare far off: it is dragged to the warrior and held a moment. */
  grapple: { ...s0, id: 'grapple', cls: 'warrior', slot: 0, cooldown: sec(7), targeting: 'enemy', castRange: 5000, damage: 40, stun: 18, ai: { use: 'fight', n: 1, reach: 5000 } },
  /** A quick fix: the most damaged structure near a point gets `amount` back at once. */
  tuneUp: { ...s0, id: 'tuneUp', cls: 'artificer', slot: 0, cooldown: sec(6), targeting: 'point', castRange: 4000, radius: 1500, amount: 150, ai: { use: 'repair', n: 1, reach: 4000 } },
  /** A lance of frost towards a point: it goes through its line, slowing what it hits for `duration`. */
  frostLance: { ...s0, id: 'frostLance', cls: 'mage', slot: 0, cooldown: sec(6), targeting: 'point', castRange: 6000, radius: 500, damage: 50, slowBp: 5000, duration: 24, ai: { use: 'pack', n: 2 } },
  /** An arrow that goes through everything in its line. */
  piercingShot: { ...s0, id: 'piercingShot', cls: 'ranger', slot: 0, cooldown: sec(6), targeting: 'point', castRange: 7000, radius: 400, damage: 45, ai: { use: 'pack', n: 2 } },
  /** A blessing on an ally: it heals (amount = bp of its life) every second for duration. */
  renew: { ...s0, id: 'renew', cls: 'cleric', slot: 0, cooldown: sec(7), targeting: 'ally', castRange: 5000, duration: sec(6), every: 12, amount: 500, ai: { use: 'heal', n: 1 } },
  /** A brutal blow at one nightmare: the more hurt the barbarian, the harder (SKILL_FX). */
  brutalBlow: { ...s0, id: 'brutalBlow', cls: 'barbarian', slot: 0, cooldown: sec(7), targeting: 'enemy', castRange: 1300, damage: 25, ai: { use: 'fight', n: 1, reach: 1300 } },
  /** A seal on a nightmare for duration: every hero striking it heals `amount` bp of the blow. */
  holySeal: { ...s0, id: 'holySeal', cls: 'paladin', slot: 0, cooldown: sec(8), targeting: 'enemy', castRange: 5000, duration: sec(8), amount: 6000, ai: { use: 'fight', n: 1, reach: 5000 } },
  /** A lash of briars ahead (a cone of radius): it drags the nightmares in towards the druid and leaves them bleeding for duration. */
  briarLash: { ...s0, id: 'briarLash', cls: 'druid', slot: 0, cooldown: sec(7), targeting: 'point', castRange: 3000, radius: 3000, damage: 30, duration: 36, stun: 24, ai: { use: 'fight', n: 2, reach: 2800 } },
  // From the tomes (medium slot).
  /** A shout of defiance: everything around is stunned a moment and turns on the warrior for duration. */
  defyShout: { ...s0, id: 'defyShout', cls: 'warrior', slot: 1, cooldown: sec(15), targeting: 'self', radius: 2500, stun: 18, duration: sec(3), ai: { use: 'around', n: 3 } },
  /** A coil planted at a point for duration: every pulse it arcs to up to `amount` nightmares within radius (lightning). */
  teslaCoil: { ...s0, id: 'teslaCoil', cls: 'artificer', slot: 1, cooldown: sec(15), targeting: 'point', castRange: 4000, radius: 2500, damage: 13, amount: 3, every: 12, duration: sec(12), ai: { use: 'pack', n: 2 } },
  /** The mage vanishes to a point; where it stood bursts in fire (radius). */
  blink: { ...s0, id: 'blink', cls: 'mage', slot: 1, cooldown: sec(12), targeting: 'point', castRange: 4000, radius: 1500, damage: 60, ai: { use: 'around', n: 3 } },
  /** A snare set at a point: the first ground nightmare to step on it is held (stun) and hurt. Up to `amount` at a time. */
  snare: { ...s0, id: 'snare', cls: 'ranger', slot: 1, cooldown: sec(10), targeting: 'point', castRange: 4000, radius: 700, damage: 30, stun: sec(2), duration: sec(30), amount: 2, ai: { use: 'pack', n: 1 } },
  /** A prayer for everyone: every ally within radius heals `amount` bp of its life. */
  sharedPrayer: { ...s0, id: 'sharedPrayer', cls: 'cleric', slot: 1, cooldown: sec(16), targeting: 'self', radius: 9000, amount: 1500, ai: { use: 'heal', n: 2 } },
  /** For duration the barbarian's blows leave its targets bleeding (SKILL_FX). */
  bloodletting: { ...s0, id: 'bloodletting', cls: 'barbarian', slot: 1, cooldown: sec(14), targeting: 'self', duration: sec(6), ai: { use: 'fight', n: 1, reach: 1500 } },
  /** The paladin leaps beside an ally: the ally takes less for duration and the nightmares around it turn on the paladin. */
  intervene: { ...s0, id: 'intervene', cls: 'paladin', slot: 1, cooldown: sec(14), targeting: 'ally', castRange: 6000, radius: 1500, duration: sec(6), ai: { use: 'allies', n: 1 } },
  /** A wall of thorns across the way (amount hexes) for duration: it slows and pricks what crosses it. */
  thornWall: { ...s0, id: 'thornWall', cls: 'druid', slot: 1, cooldown: sec(14), targeting: 'point', castRange: 5000, damage: 8, every: 6, slowBp: 5000, amount: 4, duration: sec(8), ai: { use: 'pack', n: 2 } },
  // From the tomes (ultimate slot).
  /** The last line: for duration the warrior can't fall below 1 life. */
  lastStand: { ...s0, id: 'lastStand', cls: 'warrior', slot: 2, cooldown: sec(60), targeting: 'self', radius: 3000, duration: sec(8), ai: { use: 'fight', n: 3, reach: 2000 } },
  /** A clockwork hound let loose at a point: it runs at the nightmares, bites around it and draws them (taunt) until broken or `duration`. */
  clockHound: { ...s0, id: 'clockHound', cls: 'artificer', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 3000, radius: 700, damage: 30, every: 12, amount: 500, duration: sec(25), ai: { use: 'pack', n: 3 } },
  /** A rain of fire over a point: every pulse a meteor falls somewhere in radius (SKILL_FX). */
  fireRain: { ...s0, id: 'fireRain', cls: 'mage', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 6000, radius: 3500, damage: 45, every: 4, duration: sec(6), ai: { use: 'pack', n: 4 } },
  /** The hawk's eye: for duration the ranger reaches `amount` bp as far and never misses a critical on elites and bosses. */
  eagleEye: { ...s0, id: 'eagleEye', cls: 'ranger', slot: 2, cooldown: sec(60), targeting: 'self', duration: sec(10), amount: 15000, ai: { use: 'fight', n: 3, reach: 6000 } },
  /** Intercession on an ally: if it falls within duration it gets straight back up with `amount` bp of its life. */
  intercession: { ...s0, id: 'intercession', cls: 'cleric', slot: 2, cooldown: sec(60), targeting: 'ally', castRange: 6000, duration: sec(10), amount: 5000, ai: { use: 'allies', n: 1 } },
  /** Ancestral fury: for duration each of the barbarian's blows also lands (amount bp) on everything within radius of its target. */
  ancestralFury: { ...s0, id: 'ancestralFury', cls: 'barbarian', slot: 2, cooldown: sec(60), targeting: 'self', radius: 1200, duration: sec(8), amount: 6000, ai: { use: 'fight', n: 3, reach: 1500 } },
  /** Avatar of light: for duration the paladin's blows also land (amount bp, holy) around its target, and allies near it take less (SKILL_FX). */
  avatarOfLight: { ...s0, id: 'avatarOfLight', cls: 'paladin', slot: 2, cooldown: sec(60), targeting: 'self', radius: 1200, duration: sec(10), amount: 5000, ai: { use: 'fight', n: 3, reach: 1500 } },
  /** The forest wakes at a point: living trees (amount = each one's life) fight there for duration, drawing the nightmares. */
  forestAwakening: { ...s0, id: 'forestAwakening', cls: 'druid', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 4000, radius: 900, damage: 25, every: 12, amount: 350, duration: sec(15), ai: { use: 'pack', n: 3 } },
  // The third of each slot (docs/PODERES.md §7).
  /** A shield slam ahead (a cone of radius): it hurts, pushes back and staggers what it hits. */
  shieldSlam: { ...s0, id: 'shieldSlam', cls: 'warrior', slot: 0, cooldown: sec(8), targeting: 'point', castRange: 2000, radius: 2000, damage: 35, stun: 6, ai: { use: 'fight', n: 2, reach: 1800 } },
  /** A wall of shields for duration: the warrior and the allies within radius take less (SKILL_FX). */
  shieldWall: { ...s0, id: 'shieldWall', cls: 'warrior', slot: 1, cooldown: sec(16), targeting: 'self', radius: 2500, duration: sec(5), ai: { use: 'allies', n: 2 } },
  /** The shield's echo: a ring from the warrior that stuns and hurts everything within radius. */
  shieldEcho: { ...s0, id: 'shieldEcho', cls: 'warrior', slot: 2, cooldown: sec(60), targeting: 'self', radius: 4500, damage: 60, stun: 24, ai: { use: 'around', n: 4 } },
  /** A short cone of fire ahead: it burns what it hits and leaves it burning for duration. */
  flamethrower: { ...s0, id: 'flamethrower', cls: 'artificer', slot: 0, cooldown: sec(7), targeting: 'point', castRange: 3000, radius: 3000, damage: 45, duration: 24, ai: { use: 'fight', n: 1, reach: 2900 } },
  /** A barricade of iron plates across the way (amount hexes) for duration: what crosses it is all but stopped. */
  ironBarricade: { ...s0, id: 'ironBarricade', cls: 'artificer', slot: 1, cooldown: sec(16), targeting: 'point', castRange: 4000, slowBp: 9000, amount: 3, duration: sec(10), ai: { use: 'pack', n: 2 } },
  /** Fortify: every structure within radius takes less for duration (SKILL_FX). */
  fortifyAll: { ...s0, id: 'fortifyAll', cls: 'artificer', slot: 2, cooldown: sec(60), targeting: 'self', radius: 6000, duration: sec(10), ai: { use: 'repair', n: 2, reach: 6000 } },
  /** An acid dart at a nightmare: it hurts and leaves it eaten by acid (it takes more from everything) for duration. */
  acidDart: { ...s0, id: 'acidDart', cls: 'mage', slot: 0, cooldown: sec(8), targeting: 'enemy', castRange: 6000, damage: 12, duration: sec(3), ai: { use: 'fight', n: 1, reach: 6000 } },
  /** Chains of shadow bind a nightmare and the nearest (amount in all) for duration: a hero's blow on one hurts the others too (SKILL_FX). */
  shadowChains: { ...s0, id: 'shadowChains', cls: 'mage', slot: 1, cooldown: sec(14), targeting: 'enemy', castRange: 6000, radius: 2500, amount: 3, duration: sec(5), ai: { use: 'pack', n: 3 } },
  /** A rift of the dream at a point: it drags everything within radius to its middle for duration, then bursts (shadow). */
  dreamRift: { ...s0, id: 'dreamRift', cls: 'mage', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 6000, radius: 3000, damage: 150, duration: 36, ai: { use: 'pack', n: 4 } },
  /** A leap back away from the nightmares, leaving caltrops where the ranger stood (radius) for duration. */
  disengage: { ...s0, id: 'disengage', cls: 'ranger', slot: 0, cooldown: sec(8), targeting: 'self', castRange: 2500, radius: 1200, damage: 6, every: 6, slowBp: 5000, duration: sec(6), ai: { use: 'around', n: 2 } },
  /** A flare over a point for duration: the unseen under it show, and everything there is marked (takes more). */
  signalFlare: { ...s0, id: 'signalFlare', cls: 'ranger', slot: 1, cooldown: sec(15), targeting: 'point', castRange: 7000, radius: 3000, duration: sec(8), ai: { use: 'pack', n: 3 } },
  /** The hidden shot: for duration the nightmares in melee let the ranger be and its shots hit twice as hard (SKILL_FX). */
  hiddenShot: { ...s0, id: 'hiddenShot', cls: 'ranger', slot: 2, cooldown: sec(60), targeting: 'self', duration: sec(8), ai: { use: 'fight', n: 3, reach: 6000 } },
  /** A piercing light at a nightmare: it burns it, and heals the most hurt ally near the cleric (amount). */
  piercingLight: { ...s0, id: 'piercingLight', cls: 'cleric', slot: 0, cooldown: sec(6), targeting: 'enemy', castRange: 5000, radius: 4000, damage: 40, amount: 100, ai: { use: 'fight', n: 1, reach: 5000 } },
  /** Cleansing light: the allies within radius are freed of stuns, fear, sleep, curses and poison, and heal a little (amount bp). */
  cleanse: { ...s0, id: 'cleanse', cls: 'cleric', slot: 1, cooldown: sec(14), targeting: 'self', radius: 3000, amount: 500, ai: { use: 'allies', n: 1 } },
  /** A choir: for duration the allies within radius heal part of every blow they land (amount bp). */
  choir: { ...s0, id: 'choir', cls: 'cleric', slot: 2, cooldown: sec(60), targeting: 'self', radius: 4000, duration: sec(12), amount: 2500, ai: { use: 'allies', n: 3 } },
  /** A kick: it hurts one nightmare, throws it far back and stuns it. */
  kick: { ...s0, id: 'kick', cls: 'barbarian', slot: 0, cooldown: sec(7), targeting: 'enemy', castRange: 1300, damage: 30, stun: 18, ai: { use: 'fight', n: 1, reach: 1300 } },
  /** A terrifying roar: everything within radius cowers (stun) and takes more for duration. */
  terrify: { ...s0, id: 'terrify', cls: 'barbarian', slot: 1, cooldown: sec(15), targeting: 'self', radius: 3000, stun: 24, duration: sec(3), ai: { use: 'around', n: 3 } },
  /** A duel with one nightmare (an elite or a boss if there's one in reach) for duration: it fights only the barbarian, who hits it twice as hard. */
  duel: { ...s0, id: 'duel', cls: 'barbarian', slot: 2, cooldown: sec(60), targeting: 'enemy', castRange: 3000, duration: sec(10), ai: { use: 'fight', n: 1, reach: 3000 } },
  /** Atonement: the paladin's next blow (within duration) heals the most hurt ally near it as much as it deals (amount bp). */
  atone: { ...s0, id: 'atone', cls: 'paladin', slot: 0, cooldown: sec(6), targeting: 'self', radius: 4000, duration: sec(6), amount: 15000, ai: { use: 'fight', n: 1, reach: 1500 } },
  /** A blessed weapon for an ally: for duration its blows are holy and hit harder (amount bp). */
  blessWeapon: { ...s0, id: 'blessWeapon', cls: 'paladin', slot: 1, cooldown: sec(14), targeting: 'ally', castRange: 5000, duration: sec(10), amount: 13000, ai: { use: 'allies', n: 1 } },
  /** Sacrifice: for duration the allies within radius take half their damage, and the paladin takes that half. */
  sacrifice: { ...s0, id: 'sacrifice', cls: 'paladin', slot: 2, cooldown: sec(60), targeting: 'self', radius: 3500, duration: sec(8), amount: 5000, ai: { use: 'allies', n: 2 } },
  /** A cloud of spores at a point for duration: it slows and poisons the nightmares in it every pulse. */
  sporeCloud: { ...s0, id: 'sporeCloud', cls: 'druid', slot: 0, cooldown: sec(7), targeting: 'point', castRange: 5000, radius: 1800, damage: 10, every: 12, slowBp: 6000, duration: sec(6), ai: { use: 'pack', n: 2 } },
  /** The wolf's shape for duration: the druid runs faster (amount bp) and its blows leave its targets bleeding. */
  wolfForm: { ...s0, id: 'wolfForm', cls: 'druid', slot: 1, cooldown: sec(16), targeting: 'self', duration: sec(8), amount: 15000, ai: { use: 'fight', n: 2, reach: 4500 } },
  /** The roots quake at a point: everything within radius is thrown and held (stun) and hurt, and the ground slows for duration. */
  rootQuake: { ...s0, id: 'rootQuake', cls: 'druid', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 5000, radius: 3000, damage: 40, stun: 30, slowBp: 7000, every: 12, duration: sec(8), ai: { use: 'pack', n: 4 } },
  clanRoar: { ...s0, id: 'clanRoar', cls: 'barbarian', slot: 2, cooldown: sec(60), targeting: 'self', radius: 4000, duration: sec(8), ai: { use: 'allies', n: 3 } },
  /** The guard's banner planted where the warrior stands: allies around it take less and can't be stunned for duration. */
  guardBanner: { ...s0, id: 'guardBanner', cls: 'warrior', slot: 2, cooldown: sec(60), targeting: 'self', radius: 3500, duration: sec(10), every: 12, ai: { use: 'allies', n: 3 } },
  /** A blizzard over a point: everything under it is slowed, and frost bites every pulse (it chills: lightning shatters). */
  blizzard: { ...s0, id: 'blizzard', cls: 'mage', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 6000, radius: 3000, duration: sec(8), every: 6, damage: 8, slowBp: 5000, ai: { use: 'pack', n: 4 } },
  /** A huge arrow towards a point: it goes through everything in its line, armor and all. */
  titanArrow: { ...s0, id: 'titanArrow', cls: 'ranger', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 9000, radius: 800, damage: 200, ai: { use: 'pack', n: 4 } },
  /** A pillar of dawn light at a point: holy damage to everything in radius, double to the inked. */
  dawnJudgment: { ...s0, id: 'dawnJudgment', cls: 'cleric', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 5000, radius: 3000, damage: 150, ai: { use: 'pack', n: 4 } },
  /** A sacred wall of light: every structure within radius of the paladin takes less for duration (SKILL_FX). */
  holyRampart: { ...s0, id: 'holyRampart', cls: 'paladin', slot: 2, cooldown: sec(60), targeting: 'self', radius: 8000, duration: sec(12), ai: { use: 'repair', n: 2, reach: 8000 } },
  /** A mortar: `amount` fire shells land one after the other around a point (every `every` ticks). */
  fieldMortar: { ...s0, id: 'fieldMortar', cls: 'artificer', slot: 2, cooldown: sec(60), targeting: 'point', castRange: 7000, radius: 1500, damage: 90, amount: 3, every: 9, ai: { use: 'pack', n: 4 } },
  /** A plague cloud that follows the druid for duration: the nightmares in it are poisoned every pulse. */
  plague: { ...s0, id: 'plague', cls: 'druid', slot: 2, cooldown: sec(60), targeting: 'self', radius: 2500, duration: sec(10), every: 12, damage: 8, ai: { use: 'around', n: 4 } },
};

/**
 * The learned skills' own numbers (bp: 10000 = ×1):
 * - decoy: what a nightmare striking it deals to it (it has no armor);
 * - frostOrb: how far it rolls each tick;
 * - hawk: its mark (the marked take `markBp` of every blow, for `markTicks`) and how fast it flies;
 * - guardTotem: what allies around it take;
 * - spinHammer: what the paladin takes while it whirls (it turns blows aside);
 * - repairDrone: how fast it flies and how close it mends;
 * - lowBlow: the mark it leaves; chainLightning: what each jump keeps of the blow;
 * - blindingLight: what the blinded deal, and the cone's width; spikeMine: ticks before it arms, how close sets it off, how long its slow lasts;
 * - clanRoar: what allies take and how hard they hit while it lasts (the stronger of it and other such help counts);
 * - guardBanner: what allies by it take; blizzard: none; titanArrow: none; dawnJudgment: what the inked take of it;
 * - holyRampart: what the structures it covers take; fieldMortar: how far its shells fall from the point;
 * - plague: the poison it leaves each pulse (damage every `every` ticks for `ticks`);
 * - grapple: how close to the warrior it drags; brutalBlow: the most it adds at its lowest life;
 * - briarLash: the cone's width, how far it drags, and its bleeding (damage every `every` ticks);
 * - bloodletting: the bleeding each blow leaves (damage every `every` ticks for `ticks`); intervene: what the ally takes;
 * - clockHound and forestAwakening: how far from its maker it hunts, how fast it walks, how far it draws; the trees, how many;
 * - fireRain: each meteor's blast; avatarOfLight: how far its ward reaches and what allies there take;
 * - the third of each slot: cones' widths, pushes, wards, marks and multipliers (by name).
 */
export const SKILL_FX = {
  frostOrb: { step: 220 },
  hawk: { markBp: 11500, markTicks: 36, step: 400, reach: 700 },
  guardTotem: { takenBp: 7000 },
  spinHammer: { takenBp: 5000 },
  repairDrone: { step: 300, reach: 800 },
  lowBlow: { markBp: 11500 },
  chainLightning: { fallBp: 8500 },
  blindingLight: { blindBp: 5000, coneDeg: 90 },
  spikeMine: { arm: 12, trigger: 600, slowTicks: 36 },
  clanRoar: { takenBp: 8000, damageBp: 12000 },
  guardBanner: { takenBp: 7500 },
  dawnJudgment: { inkedBp: 20000 },
  holyRampart: { takenBp: 5000 },
  fieldMortar: { gap: 1000 },
  plague: { every: 6, ticks: 24 },
  grapple: { gap: 900 },
  brutalBlow: { maxBonusBp: 8000 },
  briarLash: { coneDeg: 90, pull: 600, every: 6, bleed: 8 },
  bloodletting: { damage: 5, every: 6, ticks: 36 },
  intervene: { takenBp: 4000 },
  clockHound: { hunt: 6000, step: 260, taunt: 1800 },
  fireRain: { blast: 900 },
  avatarOfLight: { radius: 3500, takenBp: 7000 },
  forestAwakening: { count: 2, hunt: 4000, step: 120, taunt: 1500 },
  shieldSlam: { coneDeg: 100, push: 900 },
  shieldWall: { selfBp: 5000, alliesBp: 7500 },
  flamethrower: { coneDeg: 70, burn: 5, every: 6 },
  fortifyAll: { takenBp: 7000 },
  shadowChains: { shareBp: 4000 },
  dreamRift: { pull: 120, burst: 1800 },
  signalFlare: { markBp: 11500 },
  hiddenShot: { damageBp: 20000 },
  kick: { push: 2000 },
  terrify: { markBp: 11500 },
  duel: { damageBp: 20000 },
  atone: { blowBp: 15000 },
  blessWeapon: {},
};


export const HEROES: Record<HeroClass, HeroDef> = {
  warrior: { cls: 'warrior', hp: 400, attack: 24, reload: 9, range: 900, splash: 0, armorBp: 3000, speed: 1600, projectileSpeed: 0, blockCapacity: 3, skills: ['shieldBash', 'challenge', 'oath'] },
  artificer: { cls: 'artificer', hp: 280, attack: 14, reload: 10, range: 3000, splash: 0, armorBp: 1500, speed: 1500, projectileSpeed: 1100, blockCapacity: 1, skills: ['turret', 'gearGrenade', 'automaton'] },
  mage: { cls: 'mage', hp: 220, attack: 18, reload: 14, range: 5000, splash: 600, armorBp: 500, speed: 1400, projectileSpeed: 900, blockCapacity: 1, skills: ['fireball', 'frostWall', 'storm'] },
  cleric: { cls: 'cleric', hp: 300, attack: 20, reload: 11, range: 4000, splash: 0, armorBp: 2000, speed: 1500, projectileSpeed: 1000, blockCapacity: 1, skills: ['mend', 'sanctuary', 'dawn'] },
  ranger: { cls: 'ranger', hp: 250, attack: 20, reload: 8, range: 6000, splash: 0, armorBp: 1000, speed: 1900, projectileSpeed: 1300, blockCapacity: 1, skills: ['multishot', 'net', 'arrowRain'] },
  barbarian: { cls: 'barbarian', hp: 330, attack: 23, reload: 10, range: 900, splash: 0, armorBp: 2500, speed: 1800, projectileSpeed: 0, blockCapacity: 2, skills: ['whirl', 'leap', 'frenzy'] },
  paladin: { cls: 'paladin', hp: 440, attack: 30, reload: 11, range: 1000, splash: 0, armorBp: 3500, speed: 1300, projectileSpeed: 0, blockCapacity: 3, skills: ['smite', 'aegis', 'hallow'] },
  druid: { cls: 'druid', hp: 260, attack: 22, reload: 9, range: 4500, splash: 0, armorBp: 1000, speed: 1500, projectileSpeed: 1000, blockCapacity: 1, skills: ['entangle', 'bearForm', 'bloom'] },
};

/**
 * The barbarian's kit around its skills:
 * - fury (passive): +2 % damage for every 3 % of life missing, up to +50 % (a quarter of life left), and as much of
 *   `drinkBp` of what it deals back as life (all of it at full fury);
 * - frenzy: unstoppable (no stun or slow takes), attacks `attackSpeedBp` faster, moves `speedBp`
 *   faster, and gets `lifestealBp` of the damage it deals back as life; it can still fall;
 * - brute strength (bastion): its help on a building site counts `helpBp`.
 */
export const BARBARIAN = {
  /** Fury also drinks: up to `drinkBp` of what it deals comes back as life, growing with the life missing as its damage does. */
  fury: { maxBp: 5000, drinkBp: 1000 },
  frenzy: { attackSpeedBp: 14000, speedBp: 13000, lifestealBp: 4000 },
  helpBp: 20000,
};

/**
 * The paladin's kit around its skills:
 * - devotion (passive): allies this close to a standing paladin take less damage (`auraRanks.devotionTakenBp`; one aura only);
 * - smite: `bonusBp` against elites, warlords, bosses, the inked (grade 2+) and the undead; takes one grade of ink;
 * - hallowed ground: nightmares on it deal `weakBp` of their damage; its pulse (damage) burns only the inked;
 * - keep guard: a standing paladin this close to the keep spares it `keepTakenBp` of the damage.
 */
export const PALADIN = {
  devotion: { radius: 3500 },
  /**
   * The aura grows with the paladin as the skills do (ranks at SKILL_RANK_LEVELS: I, II, III): what devotion lets
   * through, what retribution strikes (bp of `damage`) and what life heals (bp of `hpBp`), per rank.
   */
  auraRanks: { devotionTakenBp: [8500, 8000, 7500], retributionBp: [10000, 13000, 16000], lifeBp: [8000, 10000, 12000] },
  /**
   * The aura the player picks for the paladin (one at a time, and paladins' auras don't add up):
   * devotion (the one above, by default), retribution (whoever strikes an ally in reach takes `damage`,
   * holy) or life (allies in reach heal `hpBp` of their life every `every` ticks).
   */
  auras: {
    retribution: { radius: 3500, damage: 4 },
    life: { radius: 3500, every: 12, hpBp: 100 },
  },
  smite: { bonusBp: 20000 },
  hallow: { weakBp: 6000 },
  keepGuard: { radius: 3500, keepTakenBp: 8500 },
};

export type PaladinAura = 'devotion' | 'retribution' | 'life';
export const PALADIN_AURAS: readonly PaladinAura[] = ['devotion', 'retribution', 'life'];

/** Nightmares the paladin's light bites harder (the undead). */
export const UNDEAD: readonly string[] = ['skeleton', 'wraith', 'archer', 'ram', 'mangonel'];

/**
 * The druid's kit around its skills:
 * - root (passive): standing still `stillTicks`, it heals `hpBp` of its life every `every` ticks;
 * - entangle: held nightmares don't move but still strike; flyers are brought down while held;
 * - bear form: life × `hpBp`, armor + `armorBp`, its blows in melee (`range`) hit around (skill radius).
 */
export const DRUID = {
  root: { stillTicks: 24, every: 12, hpBp: 100 },
  bear: { hpBp: 18000, armorBp: 1500, range: 1000 },
};

/**
 * The warrior's kit around its skills:
 * - challenge: it also reaches the nightmares striking a wall, gate or tower within `wallRadius` (they leave it to come at him);
 * - cleave (passive): its blow also lands `shareBp` on the `targets` nightmares nearest the one it strikes, within `radius`
 *   (on all of them, a horde of goblins melted on a warrior standing still at the gate; it was the
 *   least useful class in the bastion's waves, 0.22 of the average damage; with half a blow around it is level with the rest).
 */
export const WARRIOR = { challenge: { wallRadius: 7000 }, cleave: { radius: 900, shareBp: 5000, targets: 2 } };

/** Blessing (cleric, passive): heroes this close to a standing cleric hit harder (the cleric too; doesn't stack). */
export const BLESSING = { radius: 3500, damageBp: 11500 };

/**
 * The cleric's mend inspires: the mended hero (hurt or not) attacks faster for `ticks`. It doesn't add to a
 * war cry: the stronger of the two counts.
 */
export const CLERIC = { inspire: { ticks: 48, attackSpeedBp: 12000 } };

/**
 * How far a live melee hero steps out on its own to engage, from where the player left it: three tiles for a
 * common nightmare, a little over one for a boss, a warlord or an elite (so the player can still walk away from one).
 */
export const LIVE_CHASE = { reach: 3000, boss: 1300 };

/**
 * The artificer's kit around its skills:
 * - overload: its own button, on one of its own turrets (or a clockwork one), blows it up (damage = turret damage × damageBp,
 *   plus a stun), on its own `cooldown` (ticks), apart from the turret's;
 * - upkeep (passive): structures near a standing artificer slowly mend during a fight;
 * - grenade: how hard it drags (fixed per tick) — bosses, the Warden and elites aren't moved;
 * - automaton: how far it draws nightmares, how it quickens towers around it (like the old ingenuity), and for how
 *   long after it lands a boss heeds it too (`bossTicks`, as long as a warrior's challenge; then the boss lets it be).
 */
export const ARTIFICER = {
  overload: { pick: 450, radius: 1600, damageBp: 60000, stun: 18, cooldown: 24 },
  upkeep: { radius: 3000, every: 12, hp: 5 },
  grenade: { pull: 60 },
  automaton: { taunt: 2800, aura: 3500, auraBp: 5000, bossTicks: sec(4) },
};

/** Stats grow 8 % per level, multiplicative, rounded, up to `LEVEL_GROWTH_KNEE`; 3 % per level past it. */
export const LEVEL_GROWTH_BP = 10800;
export const LEVEL_GROWTH_KNEE = 11;
export const LEVEL_GROWTH_LATE_BP = 10300;
export const MAX_LEVEL = 50;
/**
 * How much of the level growth skills take (`shareBp`, 10000 = all of it), docs/BALANCE_CLASES.md: what a skill deals,
 * heals, shields or calls up grows with the hero's level as the basic attack and health do, so a class that
 * lives by its skills keeps its place at every level. What is already a share (auras, % of health) doesn't
 * need it, and what mends buildings doesn't take it (buildings don't grow with the hero).
 */
export const SKILL_LEVEL = { shareBp: 10000 };
/** A fallen hero is out for 240 ticks (20 s). */
export const RESPAWN_TICKS = sec(20);
/** Ring of the Dreamer halves it. */
export const RESPAWN_TICKS_RING = sec(10);
/** Ghost heroes hit at 70 % × activity weight. */
export const GHOST_POWER_BP = 7000;
/**
 * A ghost with the Vigil Grimoire saves its once-a-wave ultimate for a friend in trouble: one who fell
 * (and stays down a while) or one about to fall with nightmares on them. Only when it pays: a real pack
 * or an elite there for the damage ults, never leaving a busier spot, and one rescuer per friend.
 */
/** Where a bastion ghost goes looking for nightmares. */
export const GHOST_GUARD = {
  /**
   * The learned leash never takes it further than this from its post (about two cells past the gate):
   * players stand by the walls and step out a little, they don't go hunting in the woods.
   */
  maxLeash: 4500,
  /**
   * With little at the walls, a ghost goes this far from its post after a siege engine throwing at them
   * from afar (the mangonel): a player wouldn't let it knock the walls down undisturbed. It reaches the
   * walls on the far side of the bastion too, where no gate (and so no post) is near.
   */
  siegeHunt: 20000,
  /** At most this many nightmares at its post (siege engines aside) and a ghost still goes out after one. */
  siegeWhile: 2,
};

export const GHOST_RESCUE = {
  /** Under a quarter of their life with nightmares on them: about to fall. */
  lowHpBp: 2500,
  /** Nightmares this close to the friend count as on them. */
  pressR: 2500,
  /** The damage ults (and the oath) want at least this many there, or an elite. */
  minFoes: 3,
  /** How far a ghost walks to reach a friend in trouble. */
  walkMax: 8000,
  /** A friend getting up on their own sooner than this is not worth a dawn. */
  minDownTicks: sec(5),
  /** With nobody in trouble, the ultimate waits for a pack this much bigger than the learned threshold. */
  packExtra: 2,
};
/** Abandoned heroes guard their sector at 40 %. */
export const ABANDONED_POWER_BP = 4000;
/** Max live heroes per sector. */
export const MAX_LIVE_PER_SECTOR = 4;

/** Bastion roles. */
export const FORTIFY_HP_BP = 2500;
export const ENCHANT_DAMAGE_BP = 3000;
export const MAX_ENCHANTS = 2;
export type Enchant = 'fire' | 'frost' | 'lightning' | 'acid';
/** Acid needs the alchemist's laboratory (era II). */
export const ENCHANTS: Enchant[] = ['fire', 'frost', 'lightning', 'acid'];
/**
 * Fire: burn 4/12 ticks for 36 ticks; frost: slow 30 % 24 ticks; lightning: chains to 2 more targets at
 * 50 %; acid: the nightmare it eats takes 25 % more from everything for 36 ticks.
 */
export const ENCHANT_FX = {
  fire: { dot: 4, every: 12, ticks: 36 },
  frost: { slowBp: 3000, ticks: 24 },
  lightning: { chains: 2, chainBp: 5000, chainRange: 1500 },
  acid: { vulnBp: 12500, ticks: 36 },
} as const;

/** Purifying a cursed tower: a hero stands this close for this long. */
export const PURIFY_RANGE = 1200;
export const PURIFY_TICKS = sec(3);
